import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "permission", "userTypeRadio", "permissionCategory", "scopeIndicator"]
  
  connect() {
    // Set initial state for all select-all checkboxes
    this.selectAllTargets.forEach(checkbox => {
      this.updateSelectAllState(checkbox)
    })
    
    // Set up user type change listeners if they exist
    if (this.hasUserTypeRadioTarget) {
      this.updatePermissionVisibility()
    }
  }
  
  toggleCategory(event) {
    const selectAllCheckbox = event.currentTarget
    const category = selectAllCheckbox.dataset.category
    const isChecked = selectAllCheckbox.checked
    
    // Find all permission checkboxes for this category
    const categoryPermissions = this.permissionTargets.filter(
      checkbox => checkbox.dataset.category === category
    )
    
    // Set all permissions to match the select-all state
    categoryPermissions.forEach(checkbox => {
      checkbox.checked = isChecked
    })
  }
  
  permissionChanged(event) {
    const permission = event.currentTarget
    const category = permission.dataset.category
    
    // Find the select-all checkbox for this category
    const selectAllCheckbox = this.selectAllTargets.find(
      checkbox => checkbox.dataset.category === category
    )
    
    if (selectAllCheckbox) {
      this.updateSelectAllState(selectAllCheckbox)
    }
  }
  
  updateSelectAllState(selectAllCheckbox) {
    const category = selectAllCheckbox.dataset.category
    
    // Find all permission checkboxes for this category
    const categoryPermissions = this.permissionTargets.filter(
      checkbox => checkbox.dataset.category === category
    )
    
    const checkedCount = categoryPermissions.filter(cb => cb.checked).length
    const totalCount = categoryPermissions.length
    
    if (checkedCount === 0) {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = false
    } else if (checkedCount === totalCount) {
      selectAllCheckbox.checked = true
      selectAllCheckbox.indeterminate = false
    } else {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = true
    }
  }
  
  userTypeChanged() {
    this.updatePermissionVisibility()
    this.updateScopeIndicators()
  }
  
  updatePermissionVisibility() {
    // Get selected user type (single radio button)
    const selectedRadio = this.userTypeRadioTargets.find(radio => radio.checked)
    const selectedUserTypes = selectedRadio ? [selectedRadio.value] : []
    
    if (selectedUserTypes.length === 0) {
      // If no user types selected, show all permissions
      this.showAllPermissions()
      return
    }
    
    // Load permissions configuration
    this.loadUserTypePermissions().then(userTypePermissions => {
      // Get permissions available to selected user types
      const availablePermissions = this.getAvailablePermissions(selectedUserTypes, userTypePermissions)
      
      // Update visibility of permission categories and actions
      this.updatePermissionDisplay(availablePermissions)
    })
  }
  
  async loadUserTypePermissions() {
    // This would ideally come from a data attribute or API endpoint
    // For now, we'll use a simplified approach
    const permissionsElement = document.querySelector('[data-user-type-permissions]')
    if (permissionsElement) {
      return JSON.parse(permissionsElement.dataset.userTypePermissions)
    }
    return {}
  }
  
  getAvailablePermissions(userTypes, userTypePermissions) {
    const permissions = {}
    
    userTypes.forEach(userType => {
      const typePermissions = userTypePermissions[userType] || {}
      Object.keys(typePermissions).forEach(category => {
        if (!permissions[category]) {
          permissions[category] = new Set()
        }
        typePermissions[category].forEach(action => {
          permissions[category].add(action)
        })
      })
    })
    
    // Convert sets to arrays
    Object.keys(permissions).forEach(category => {
      permissions[category] = Array.from(permissions[category])
    })
    
    return permissions
  }
  
  updatePermissionDisplay(availablePermissions) {
    // Update each permission checkbox
    this.permissionTargets.forEach(checkbox => {
      const category = checkbox.dataset.category
      const action = checkbox.value
      
      const isAvailable = availablePermissions[category]?.includes(action)
      
      // Show/hide the permission
      const permissionContainer = checkbox.closest('.flex')
      if (permissionContainer) {
        permissionContainer.style.display = isAvailable ? 'flex' : 'none'
      }
      
      // Uncheck if not available
      if (!isAvailable) {
        checkbox.checked = false
      }
    })
    
    // Update category visibility
    if (this.hasPermissionCategoryTarget) {
      this.permissionCategoryTargets.forEach(categoryElement => {
        const category = categoryElement.dataset.category
        const hasVisiblePermissions = availablePermissions[category]?.length > 0
        categoryElement.style.display = hasVisiblePermissions ? 'block' : 'none'
      })
    }
    
    // Update select-all checkboxes
    this.selectAllTargets.forEach(checkbox => {
      this.updateSelectAllState(checkbox)
    })
  }
  
  showAllPermissions() {
    // Show all permissions
    this.permissionTargets.forEach(checkbox => {
      const permissionContainer = checkbox.closest('.flex')
      if (permissionContainer) {
        permissionContainer.style.display = 'flex'
      }
    })
    
    // Show all categories
    if (this.hasPermissionCategoryTarget) {
      this.permissionCategoryTargets.forEach(categoryElement => {
        categoryElement.style.display = 'block'
      })
    }
  }
  
  async updateScopeIndicators() {
    // Get selected user type
    const selectedRadio = this.userTypeRadioTargets.find(radio => radio.checked)
    const selectedUserType = selectedRadio ? selectedRadio.value : null
    
    if (!selectedUserType || !this.hasScopeIndicatorTarget) {
      return
    }
    
    // Load permissions configuration
    const permissionsConfig = await this.loadPermissionsConfig()
    
    // Update each scope indicator
    this.scopeIndicatorTargets.forEach(indicator => {
      const permissionPath = indicator.dataset.permission
      const [category, action] = permissionPath.split('.')
      
      const scope = permissionsConfig?.permissions?.[category]?.actions?.[action]?.default_access?.[selectedUserType]
      
      if (scope === false || scope === null || scope === undefined) {
        indicator.textContent = '[no access]'
        indicator.className = 'text-red-500'
      } else if (scope === true) {
        indicator.textContent = '[full access]'
        indicator.className = 'text-green-600'
      } else {
        indicator.textContent = `[${scope}]`
        indicator.className = 'text-blue-600'
      }
    })
  }
  
  async loadPermissionsConfig() {
    // Try to get permissions config from the server
    try {
      const response = await fetch('/admin/permissions_config.json')
      if (response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.warn('Could not load permissions config:', error)
    }
    
    // Fallback: use embedded config if available
    const configElement = document.querySelector('[data-permissions-config]')
    if (configElement) {
      return JSON.parse(configElement.dataset.permissionsConfig)
    }
    
    return null
  }
}